import React, { useState, useEffect } from 'react';
import { Calculator, TrendingUp, DollarSign, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const TaxCalculator = () => {
  const [indicators, setIndicators] = useState({ uf: 0, utm: 0 });
  const [loading, setLoading] = useState(true);
  
  // Boletas de Honorarios
  const [honorariosInput, setHonorariosInput] = useState('');
  const [honorariosMode, setHonorariosMod] = useState('bruto'); // bruto o liquido
  
  // IVA Calculator
  const [ivaInput, setIvaInput] = useState('');
  const [ivaMode, setIvaMode] = useState('agregar'); // agregar o desglosar

  // Fetch indicators from mindicador.cl
  useEffect(() => {
    const fetchIndicators = async () => {
      try {
        const response = await fetch('https://mindicador.cl/api');
        const data = await response.json();
        setIndicators({
          uf: data.uf.valor,
          utm: data.utm.valor,
          lastUpdate: new Date(data.uf.fecha).toLocaleDateString('es-CL')
        });
        setLoading(false);
      } catch (error) {
        console.error('Error fetching indicators:', error);
        setLoading(false);
      }
    };

    fetchIndicators();
    // Refresh every 30 minutes
    const interval = setInterval(fetchIndicators, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Cálculos Boletas de Honorarios (Ley 21.133 - 15.25% desde enero 2026)
  const RETENCION = 0.1525;
  
  const calcularHonorarios = () => {
    const monto = parseFloat(honorariosInput) || 0;
    if (honorariosMode === 'bruto') {
      const retencion = monto * RETENCION;
      const liquido = monto - retencion;
      return { bruto: monto, retencion, liquido };
    } else {
      const bruto = monto / (1 - RETENCION);
      const retencion = bruto * RETENCION;
      return { bruto, retencion, liquido: monto };
    }
  };

  // Cálculos IVA (19% en Chile)
  const IVA_RATE = 0.19;
  
  const calcularIVA = () => {
    const monto = parseFloat(ivaInput) || 0;
    if (ivaMode === 'agregar') {
      const iva = monto * IVA_RATE;
      const total = monto + iva;
      return { neto: monto, iva, total };
    } else {
      const neto = monto / (1 + IVA_RATE);
      const iva = monto - neto;
      return { neto, iva, total: monto };
    }
  };

  const formatCLP = (value) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(value);
  };

  const honorariosResult = calcularHonorarios();
  const ivaResult = calcularIVA();

  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-black rounded-full mb-4">
            <Calculator className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
            Calculadora Tributaria
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Calcula boletas de honorarios, IVA y consulta indicadores económicos actualizados en tiempo real
          </p>
        </div>

        {/* Indicadores en Tiempo Real */}
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-12">
          <Card className="border-t-4 border-[#ef4444] hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">UF (Unidad de Fomento)</p>
                  <p className="text-3xl font-bold text-black">
                    {loading ? '...' : formatCLP(indicators.uf)}
                  </p>
                  {!loading && (
                    <p className="text-xs text-slate-500 mt-1">
                      Actualizado: {indicators.lastUpdate}
                    </p>
                  )}
                </div>
                <TrendingUp className="h-12 w-12 text-[#ef4444]" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-t-4 border-[#ef4444] hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">UTM (Unidad Tributaria Mensual)</p>
                  <p className="text-3xl font-bold text-black">
                    {loading ? '...' : formatCLP(indicators.utm)}
                  </p>
                  {!loading && (
                    <p className="text-xs text-slate-500 mt-1">
                      Actualizado: {indicators.lastUpdate}
                    </p>
                  )}
                </div>
                <DollarSign className="h-12 w-12 text-[#ef4444]" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Calculadoras */}
        <div className="max-w-5xl mx-auto">
          <Tabs defaultValue="honorarios" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-8">
              <TabsTrigger value="honorarios" className="text-lg">
                <FileText className="mr-2 h-5 w-5" />
                Boletas de Honorarios
              </TabsTrigger>
              <TabsTrigger value="iva" className="text-lg">
                <Calculator className="mr-2 h-5 w-5" />
                Calculadora IVA
              </TabsTrigger>
            </TabsList>

            {/* Calculadora Boletas de Honorarios */}
            <TabsContent value="honorarios">
              <Card className="shadow-xl border-t-4 border-[#ef4444]">
                <CardHeader className="bg-black text-white">
                  <CardTitle className="text-2xl">CALCULADORA DE BOLETAS DE HONORARIOS</CardTitle>
                  <p className="text-sm text-slate-200 mt-2">
                    Retención actualizada: 15.25% (Ley 21.133 - vigente desde enero 2026).
                  </p>
                </CardHeader>
                <CardContent className="p-8">
                  <div className="space-y-6">
                    {/* Mode Selector */}
                    <div className="flex gap-4">
                      <button
                        onClick={() => setHonorariosMod('bruto')}
                        className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                          honorariosMode === 'bruto'
                            ? 'bg-black text-white shadow-lg'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        Calcular desde Monto Bruto
                      </button>
                      <button
                        onClick={() => setHonorariosMod('liquido')}
                        className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                          honorariosMode === 'liquido'
                            ? 'bg-black text-white shadow-lg'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        Calcular desde Líquido Deseado
                      </button>
                    </div>

                    {/* Input */}
                    <div>
                      <Label htmlFor="honorarios-input" className="text-lg font-semibold text-slate-700">
                        {honorariosMode === 'bruto' ? 'Monto Bruto a Cobrar' : 'Líquido que Deseas Recibir'}
                      </Label>
                      <Input
                        id="honorarios-input"
                        type="number"
                        placeholder="Ingresa el monto"
                        value={honorariosInput}
                        onChange={(e) => setHonorariosInput(e.target.value)}
                        className="mt-2 text-2xl h-14 border-2 border-slate-300 focus:border-black"
                      />
                    </div>

                    {/* Results */}
                    {honorariosInput && (
                      <div className="bg-slate-50 rounded-lg p-6 space-y-4 border-2 border-slate-200">
                        <div className="flex justify-between items-center pb-3 border-b border-slate-300">
                          <span className="text-slate-600 font-medium">Monto Bruto:</span>
                          <span className="text-2xl font-bold text-black">
                            {formatCLP(honorariosResult.bruto)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center pb-3 border-b border-slate-300">
                          <span className="text-slate-600 font-medium">
                            Retención (15.25%):
                          </span>
                          <span className="text-xl font-semibold text-[#ef4444]">
                            - {formatCLP(honorariosResult.retencion)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center pt-2">
                          <span className="text-lg font-bold text-slate-700">Líquido a Recibir:</span>
                          <span className="text-3xl font-bold text-green-600">
                            {formatCLP(honorariosResult.liquido)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Calculadora IVA */}
            <TabsContent value="iva">
              <Card className="shadow-xl border-t-4 border-[#ef4444]">
                <CardHeader className="bg-black text-white">
                  <CardTitle className="text-2xl">CALCULADORA DE IVA</CardTitle>
                  <p className="text-sm text-slate-200 mt-2">
                    IVA en Chile: 19%.
                  </p>
                </CardHeader>
                <CardContent className="p-8">
                  <div className="space-y-6">
                    {/* Mode Selector */}
                    <div className="flex gap-4">
                      <button
                        onClick={() => setIvaMode('agregar')}
                        className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                          ivaMode === 'agregar'
                            ? 'bg-black text-white shadow-lg'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        Agregar IVA al Neto
                      </button>
                      <button
                        onClick={() => setIvaMode('desglosar')}
                        className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                          ivaMode === 'desglosar'
                            ? 'bg-black text-white shadow-lg'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        Desglosar IVA del Total
                      </button>
                    </div>

                    {/* Input */}
                    <div>
                      <Label htmlFor="iva-input" className="text-lg font-semibold text-slate-700">
                        {ivaMode === 'agregar' ? 'Monto Neto (sin IVA)' : 'Monto Total (con IVA)'}
                      </Label>
                      <Input
                        id="iva-input"
                        type="number"
                        placeholder="Ingresa el monto"
                        value={ivaInput}
                        onChange={(e) => setIvaInput(e.target.value)}
                        className="mt-2 text-2xl h-14 border-2 border-slate-300 focus:border-black"
                      />
                    </div>

                    {/* Results */}
                    {ivaInput && (
                      <div className="bg-slate-50 rounded-lg p-6 space-y-4 border-2 border-slate-200">
                        <div className="flex justify-between items-center pb-3 border-b border-slate-300">
                          <span className="text-slate-600 font-medium">Monto Neto:</span>
                          <span className="text-2xl font-bold text-black">
                            {formatCLP(ivaResult.neto)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center pb-3 border-b border-slate-300">
                          <span className="text-slate-600 font-medium">
                            IVA (19%):
                          </span>
                          <span className="text-xl font-semibold text-[#ef4444]">
                            + {formatCLP(ivaResult.iva)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center pt-2">
                          <span className="text-lg font-bold text-slate-700">Total con IVA:</span>
                          <span className="text-3xl font-bold text-green-600">
                            {formatCLP(ivaResult.total)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Disclaimer */}
          <Card className="mt-8 bg-amber-50 border-amber-200">
            <CardContent className="p-6">
              <p className="text-sm text-amber-900">
                <strong>Nota importante:</strong> Estos cálculos son referenciales y tienen fines informativos. 
                La retención del 15.25% en boletas de honorarios rige desde enero 2026 según Ley 21.133. 
                Para casos específicos o situaciones especiales, consulta con un profesional contable.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default TaxCalculator;
